// @flow
import memoize from 'mz-utils/memoize'

// Constants
const INFINITY = 1 / 0
const charCodeOfDot = '.'.charCodeAt(0)
const reEscapeChar = /\\(\\)?/g
const rePropName = RegExp(
  // Match anything that isn't a dot or bracket.
  '[^.[\\]]+' +
    '|' +
    // Or match property names within brackets.
    '\\[(?:' +
    // Match a non-string expression.
    '([^"\'][^[]*)' +
    '|' +
    // Or match strings (supports escaping characters).
    '(["\'])((?:(?!\\2)[^\\\\]|\\\\.)*?)\\2' +
    ')\\]' +
    '|' +
    // Or match "" as the space between consecutive dots or empty brackets.
    '(?=(?:\\.|\\[\\])(?:\\.|\\[\\]|$))',
  'g'
)

const stringToPath = memoize(
  (string: string): string[] => {
    const result = []
    if (string.charCodeAt(0) === charCodeOfDot) {
      result.push('')
    }
    string.replace(rePropName, (match, expression, quote, subString) => {
      let key = match
      if (quote) {
        key = subString.replace(reEscapeChar, '$1')
      } else if (expression) {
        key = expression.trim()
      }
      result.push(key)
      return ''
    })
    return result
  },
  (x) => x
)

function toKey(value) {
  if (typeof value === 'string') {
    return value
  }
  const result = `${value}`
  // eslint-disable-next-line eqeqeq
  return result == '0' && 1 / value == -INFINITY ? '-0' : result
}

/**
 * Converts `value` to a property path array.
 *
 * @since 4.0.0
 * @category Util
 * @param {*} value The value to convert.
 * @returns {Array} Returns the new property path array.
 * @example
 *
 * toPath('a.b.c')
 * // => ['a', 'b', 'c']
 *
 * toPath('a[0].b.c')
 * // => ['a', '0', 'b', 'c']
 */
export default function toPath(value: string | string[]): string[] {
  if (Array.isArray(value)) {
    return value.map(toKey)
  }
  return stringToPath(value).slice()
}
